import { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { pick } from "lodash-es";
import { type Ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import useTeamPicker from "shared/composables/useTeamPicker";
import { type ChartIndex } from "shared/modules/Charts/types";
import { type InstantInsightsChart } from "shared/modules/Charts/types/chartTypes";
import { type ChartLibraryRequestParams } from "shared/modules/Charts/types/data";
import {
  isChartSettingWithExportOptions,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import { InstantInsightsReport } from "shared/resources";
import StreamService, {
  type InsightsResponse,
} from "shared/services/api/StreamService";
import features from "shared/services/features";
import { type Nullable } from "shared/types";

import { type InsightsServiceInterface } from "./InsightsServiceFactory";

class InsightsService implements InsightsServiceInterface {
  readonly isPublic = false;

  async chartsConfig(): Promise<ChartIndex> {
    const { data } = await $streemApiV1.get<ChartIndex>("charts_config");

    return (Object.keys(data) as InstantInsightsChart[]).reduce(
      (acc, configKey) => {
        const config = data[configKey];

        acc[configKey] = {
          ...pick(config, [
            "info_tooltip",
            "hide_streams",
            "display_pagination",
            "requires_analytics_integration",
            "has_double_width",
            "chart_variants",
            "default_chart_variant",
            "settings_component",
            "metric_labels",
            "metric_tooltips",
          ]),
          label: config.label ?? "",
          ...(isChartSettingWithStreamsAndMediaTypes(config)
            ? { stream_ids: [] }
            : {}),
          ...(isChartSettingWithExportOptions(config)
            ? {
                export_options: Array.isArray(config.export_options)
                  ? config.export_options
                  : [],
              }
            : {}),
        };

        return acc;
      },
      {} as ChartIndex
    );
  }

  async dashboard(
    id?: number,
    options?: { signal?: AbortSignal }
  ): Promise<Nullable<InstantInsightsReport>> {
    if (!id) {
      throw new Error("Private service requires an ID");
    }

    const { selectedTeamPreference } = useTeamPicker() as unknown as {
      selectedTeamPreference: Ref<number>;
    };

    const response = await InstantInsightsReport.withDashboardScope()
      .extraFetchOptions({
        signal: options?.signal,
      })
      .where({
        id,
        viewedItems: {
          feature: "has_insights_plus_teasers",
        },
        ...(features.has("has_team_workspaces")
          ? { organisationTeamId: [selectedTeamPreference.value, "null"] }
          : {}),
      })
      .first();

    return response.data ?? null;
  }

  streamInsights(
    streamId: string | number,
    options?: AxiosRequestConfig<ChartLibraryRequestParams>
  ): Promise<AxiosResponse<InsightsResponse>> {
    return StreamService.insights(Number(streamId), options);
  }
}

export default InsightsService;
